/**
 * localStorage helpers for auth tokens and UI preferences.
 *
 * Tokens live in localStorage (Bearer-header pattern, same as adminpanel).
 * The httpOnly-cookie flow is NOT used: POST /auth/refresh only accepts the
 * refresh token in the request body, so a cookie-only client cannot refresh.
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'flock_auth_token',
  REFRESH_TOKEN: 'flock_refresh_token',
  CURRENT_ORG_ID: 'flock_current_org_id',
} as const;

export function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

export function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
}

export function getCurrentOrgId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.CURRENT_ORG_ID);
}

export function setCurrentOrgId(orgId: string): void {
  localStorage.setItem(STORAGE_KEYS.CURRENT_ORG_ID, orgId);
}

export function removeCurrentOrgId(): void {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_ORG_ID);
}

/** Per-user key for the last selected group (kept across logouts, like mobile). */
export function getLastSelectedGroupKey(userId: string): string {
  return `flock_last_group_${userId}`;
}

export function getLastSelectedGroupId(userId: string): string | null {
  return localStorage.getItem(getLastSelectedGroupKey(userId));
}

export function setLastSelectedGroupId(userId: string, groupId: string | null): void {
  const key = getLastSelectedGroupKey(userId);
  if (groupId) {
    localStorage.setItem(key, groupId);
  } else {
    localStorage.removeItem(key);
  }
}

/**
 * Clear auth tokens. Intentionally keeps CURRENT_ORG_ID and last-selected
 * group (non-sensitive UUIDs) so the user lands back in the same org/group
 * after re-login — mirrors mobile's signOut behavior.
 */
export function clearAuthTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/** Decode a JWT payload without verification (client-side expiry checks only). */
export function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, bufferMs = 0): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 < Date.now() + bufferMs;
}
